import React, { useState } from 'react';
import { getWebsiteUnits, getContact, isSold, getUnitName } from './websiteStyles';
import AdvancedFilterModal from './AdvancedFilterModal';
import Pagination from '../ui/Pagination';

const INK = '#14161C';
const INK2 = '#52545C';
const INK3 = '#8B8D94';
const INK4 = '#B0B2B8';
const LINE = '#E5E5E3';
const PAPER2 = '#F3F3F1';
const BLUE = '#2563EB';
const GREEN = '#22C55E';
const heading = '"Inter Tight", sans-serif';
const mono = '"JetBrains Mono", monospace';

function WebsiteUsedCars({ onViewUnit }) {
  const [makeFilters, setMakeFilters] = useState([]);
  const [sortBy, setSortBy] = useState('newest');
  const [gridView, setGridView] = useState(true);
  const [search, setSearch] = useState('');
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(20);
  const isMobile = typeof window !== 'undefined' && window.innerWidth < 600;

  const contact = getContact();
  const allUnits = getWebsiteUnits();
  const vehicles = allUnits.filter(u => u._type === 'vehicle' && !u.cutType);

  // Compute make counts
  const makeCounts = {};
  vehicles.forEach(u => { if (u.make) makeCounts[u.make] = (makeCounts[u.make] || 0) + 1; });
  const makes = Object.keys(makeCounts).sort();

  // Filter
  let filtered = vehicles;
  if (makeFilters.length > 0) filtered = filtered.filter(u => makeFilters.includes(u.make));
  if (search) {
    const q = search.toLowerCase();
    filtered = filtered.filter(u => [u.make, u.model, u.chassis, u.sku, u.color].filter(Boolean).join(' ').toLowerCase().includes(q));
  }

  // Sort
  const sortFns = {
    newest: (a, b) => (b.createdAt || '').localeCompare(a.createdAt || ''),
    oldest: (a, b) => (a.createdAt || '').localeCompare(b.createdAt || ''),
    makeAZ: (a, b) => (a.make || '').localeCompare(b.make || ''),
    kmLow: (a, b) => (a.mileage || 999999) - (b.mileage || 999999),
    kmHigh: (a, b) => (b.mileage || 0) - (a.mileage || 0),
  };
  filtered.sort((a, b) => {
    const soldDiff = (isSold(a) ? 1 : 0) - (isSold(b) ? 1 : 0);
    if (soldDiff !== 0) return soldDiff;
    return (sortFns[sortBy] || sortFns.newest)(a, b);
  });

  const available = filtered.filter(u => !isSold(u)).length;

  const toggleMake = (m) => {
    setMakeFilters(prev => prev.includes(m) ? prev.filter(x => x !== m) : [...prev, m]);
  };

  const waMsg = (unit) => encodeURIComponent(`Hi, I'm interested in ${getUnitName(unit)}${unit.sku ? ' (' + unit.sku + ')' : ''}. Can you share pricing and details?`);

  return (
    <div>
      {/* Header */}
      <section style={{ maxWidth: 1440, margin: '0 auto', padding: '40px 24px 0' }}>
        <div style={{ fontFamily: mono, fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase', color: BLUE, marginBottom: 8, display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ width: 5, height: 5, borderRadius: '50%', background: BLUE }}></span>
          Inventory
        </div>
        <h1 style={{ fontFamily: heading, fontWeight: 700, letterSpacing: '-0.04em', fontSize: 'clamp(32px, 5vw, 48px)', lineHeight: 1, color: INK, marginBottom: 16 }}>
          Used cars
        </h1>
        <p style={{ fontSize: 15, color: INK3, maxWidth: 560, lineHeight: 1.6 }}>
          Every vehicle hand-picked, GCC or US spec, ready for inspection at the yard. Prices on inquiry — WhatsApp or call for a tailored quote.
        </p>
      </section>

      {/* Toolbar */}
      <section style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 24px 0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ fontFamily: mono, fontSize: 12, color: INK3 }}>
            {available} available · {filtered.length} total
          </div>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            {/* Search */}
            <div style={{ position: 'relative' }}>
              <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search..." style={{
                height: 36, padding: '0 12px 0 32px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff',
                fontFamily: heading, fontSize: 13, color: INK, outline: 'none', width: 180,
              }} />
              <svg viewBox="0 0 24 24" fill="none" stroke={INK4} strokeWidth="2" style={{ width: 14, height: 14, position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)' }}><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </div>
            {/* Advanced filter */}
            <button onClick={() => setAdvancedOpen(true)} style={{ height: 36, padding: '0 14px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff', fontFamily: heading, fontSize: 13, fontWeight: 500, color: INK2, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ width: 14, height: 14 }}><path d="M12 3v18M3 12h18"/></svg>
              Advanced
            </button>
            {/* Sort */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontFamily: heading, fontSize: 13, color: INK3 }}>Sort:</span>
              <select value={sortBy} onChange={e => setSortBy(e.target.value)} style={{
                height: 36, padding: '0 10px', borderRadius: 8, border: `1px solid ${LINE}`, background: '#fff',
                fontFamily: heading, fontSize: 13, color: INK, outline: 'none', cursor: 'pointer',
              }}>
                <option value="newest">Newest</option>
                <option value="oldest">Oldest</option>
                <option value="makeAZ">Make A-Z</option>
                <option value="kmLow">KM Low-High</option>
                <option value="kmHigh">KM High-Low</option>
              </select>
            </div>
            {/* View toggle */}
            <div style={{ display: 'flex', borderRadius: 8, border: `1px solid ${LINE}`, overflow: 'hidden' }}>
              <button onClick={() => setGridView(true)} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', cursor: 'pointer', background: gridView ? INK : '#fff' }}>
                <svg viewBox="0 0 16 16" fill={gridView ? '#fff' : INK3} style={{ width: 14, height: 14 }}><rect x="1" y="1" width="6" height="6" rx="1"/><rect x="9" y="1" width="6" height="6" rx="1"/><rect x="1" y="9" width="6" height="6" rx="1"/><rect x="9" y="9" width="6" height="6" rx="1"/></svg>
              </button>
              <button onClick={() => setGridView(false)} style={{ width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', border: 'none', borderLeft: `1px solid ${LINE}`, cursor: 'pointer', background: !gridView ? INK : '#fff' }}>
                <svg viewBox="0 0 16 16" fill={!gridView ? '#fff' : INK3} style={{ width: 14, height: 14 }}><rect x="1" y="1" width="14" height="3" rx="1"/><rect x="1" y="6.5" width="14" height="3" rx="1"/><rect x="1" y="12" width="14" height="3" rx="1"/></svg>
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* Main content: sidebar + grid */}
      <section style={{ maxWidth: 1440, margin: '0 auto', padding: '24px 24px 80px' }}>
        <div style={{ display: 'flex', gap: 32 }} className="ws-cars-layout">
          {/* Sidebar filters */}
          <div style={{ width: 220, flexShrink: 0 }} className="ws-cars-sidebar">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <span style={{ fontFamily: heading, fontSize: 14, fontWeight: 600, color: INK }}>Filters</span>
              {makeFilters.length > 0 && (
                <button onClick={() => setMakeFilters([])} style={{ fontFamily: heading, fontSize: 12, color: BLUE, background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}>Reset</button>
              )}
            </div>

            {/* Make filter */}
            <div style={{ marginBottom: 24 }}>
              <div style={{ fontFamily: heading, fontSize: 13, fontWeight: 600, color: INK, marginBottom: 12, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                Make
                <span style={{ fontSize: 16, color: INK4, cursor: 'pointer' }}>▴</span>
              </div>
              <div style={{ maxHeight: 280, overflowY: 'auto' }}>
                {makes.map(m => (
                  <label key={m} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', cursor: 'pointer', fontSize: 13, color: INK2 }}>
                    <input type="checkbox" checked={makeFilters.includes(m)} onChange={() => toggleMake(m)} style={{ width: 16, height: 16, accentColor: BLUE, cursor: 'pointer' }} />
                    <span style={{ flex: 1 }}>{m}</span>
                    <span style={{ fontFamily: mono, fontSize: 11, color: INK4 }}>{makeCounts[m]}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          {/* Grid */}
          <div style={{ flex: 1, minWidth: 0 }}>
            {/* Pagination (top) */}
            {filtered.length > 0 && <Pagination total={filtered.length} page={page} perPage={perPage}
              onPageChange={p => { setPage(p); window.scrollTo(0, 0); }} onPerPageChange={n => { setPerPage(n); setPage(1); }} theme="website" />}
            {filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '80px 0', color: INK3 }}>
                <div style={{ fontSize: 40, marginBottom: 12 }}>🔍</div>
                <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 600, color: INK, marginBottom: 6 }}>No vehicles found</div>
                <div style={{ fontSize: 14 }}>Try adjusting your filters.</div>
              </div>
            ) : (
              <div style={{ display: 'grid', gridTemplateColumns: gridView ? (isMobile ? '1fr' : 'repeat(auto-fill, minmax(280px, 1fr))') : '1fr', gap: isMobile ? 12 : 16 }}>
                {filtered.slice((page - 1) * perPage, page * perPage).map(u => {
                  const name = getUnitName(u);
                  const sold = isSold(u);
                  const reserved = u.website_status === 'reserved';
                  const arriving = u.website_status === 'arriving';
                  const img = u.images?.[0];
                  const km = u.mileage ? (u.mileage >= 1000 ? (u.mileage / 1000).toFixed(1).replace('.0', '') + 'k' : u.mileage) : null;

                  if (!gridView) {
                    // List view
                    return (
                      <div key={u.id} onClick={() => onViewUnit(u)} style={{ display: 'flex', gap: 16, background: '#fff', borderRadius: 12, border: `1px solid ${LINE}`, overflow: 'hidden', cursor: 'pointer', transition: 'box-shadow 0.2s' }}
                        onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 4px 16px rgba(0,0,0,0.06)'; }}
                        onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; }}>
                        <div style={{ width: 200, height: 140, background: PAPER2, flexShrink: 0, overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          {img ? <img src={img} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover', ...(sold ? { opacity: 0.5, filter: 'grayscale(40%)' } : {}) }} /> : <span style={{ fontSize: 40, opacity: 0.3 }}>🚗</span>}
                        </div>
                        <div style={{ flex: 1, padding: '16px 16px 16px 0', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                          <div style={{ fontFamily: mono, fontSize: 10, color: INK3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{[u.year, u.make].filter(Boolean).join(' · ')}</div>
                          <div style={{ fontFamily: heading, fontSize: 20, fontWeight: 600, color: INK, marginBottom: 8 }}>{u.model || name}</div>
                          <div style={{ display: 'flex', gap: 20, alignItems: 'center' }}>
                            {km && <span style={{ fontFamily: mono, fontSize: 12, color: INK3 }}>KM {km}</span>}
                            {u.fuel && <span style={{ fontFamily: mono, fontSize: 12, color: INK3 }}>Fuel {u.fuel}</span>}
                            {u.transmission && <span style={{ fontFamily: mono, fontSize: 12, color: INK3 }}>Trans {u.transmission}</span>}
                            {sold && <span style={{ padding: '3px 8px', borderRadius: 4, background: '#FEE2E2', color: '#DC2626', fontFamily: mono, fontSize: 10, fontWeight: 600 }}>SOLD</span>}
                          </div>
                        </div>
                      </div>
                    );
                  }

                  // Grid view card
                  return (
                    <div key={u.id} onClick={() => onViewUnit(u)} style={{ background: '#fff', borderRadius: 12, overflow: 'hidden', cursor: 'pointer', border: `1px solid ${LINE}`, transition: 'box-shadow 0.25s, transform 0.25s' }}
                      onMouseEnter={e => { e.currentTarget.style.boxShadow = '0 8px 32px rgba(0,0,0,0.08)'; e.currentTarget.style.transform = 'translateY(-2px)'; }}
                      onMouseLeave={e => { e.currentTarget.style.boxShadow = 'none'; e.currentTarget.style.transform = ''; }}>
                      <div style={{ position: 'relative', aspectRatio: '4/3', background: PAPER2, overflow: 'hidden' }}>
                        {img ? <img src={img} alt={name} loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover', ...(sold ? { opacity: 0.5, filter: 'grayscale(40%)' } : {}) }} /> : <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 48, opacity: 0.2 }}>🚗</div>}
                        <div style={{ position: 'absolute', top: 10, left: 10, display: 'flex', gap: 5 }}>
                          {u.is_featured && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#DCFCE7', color: '#16A34A', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>● Featured</span>}
                          {reserved && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEF3C7', color: '#D97706', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Reserved</span>}
                          {arriving && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#DBEAFE', color: '#2563EB', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Arriving</span>}
                          {sold && <span style={{ padding: '4px 10px', borderRadius: 6, background: '#FEE2E2', color: '#DC2626', fontSize: 10, fontWeight: 700, fontFamily: mono, letterSpacing: '0.06em', textTransform: 'uppercase' }}>Sold</span>}
                        </div>
                        {u.color && <div style={{ position: 'absolute', top: 10, right: 10, padding: '3px 7px', borderRadius: 4, background: 'rgba(0,0,0,0.45)', backdropFilter: 'blur(4px)', fontFamily: mono, fontSize: 9, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{u.color}</div>}
                      </div>
                      <div style={{ padding: '14px 16px 16px' }}>
                        <div style={{ fontFamily: mono, fontSize: 10, color: INK3, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 4 }}>{[u.year, u.make].filter(Boolean).join(' · ')}</div>
                        <div style={{ fontFamily: heading, fontSize: 18, fontWeight: 600, color: INK, lineHeight: 1.2, marginBottom: 12, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{u.model || name}</div>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                          <div style={{ display: 'flex', gap: 16 }}>
                            {km && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>KM</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{km}</div></div>}
                            {u.fuel && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Fuel</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{u.fuel}</div></div>}
                            {u.transmission && <div><div style={{ fontFamily: mono, fontSize: 9, color: INK4, letterSpacing: '0.08em', textTransform: 'uppercase' }}>Trans</div><div style={{ fontFamily: mono, fontSize: 13, fontWeight: 500, color: INK2, marginTop: 2 }}>{u.transmission}</div></div>}
                          </div>
                          {!sold && (
                            <a href={`https://wa.me/${contact.whatsapp}?text=${waMsg(u)}`} target="_blank" rel="noopener noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '6px 14px', borderRadius: 9999, background: GREEN, color: '#fff', fontSize: 12, fontWeight: 600, textDecoration: 'none' }}>
                              <span style={{ width: 6, height: 6, borderRadius: '50%', background: '#fff', opacity: 0.7 }}></span> Inquire
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
            {/* Pagination (bottom) */}
            {filtered.length > 0 && <Pagination total={filtered.length} page={page} perPage={perPage}
              onPageChange={p => { setPage(p); window.scrollTo(0, 0); }} onPerPageChange={n => { setPerPage(n); setPage(1); }} theme="website" />}
          </div>
        </div>
      </section>

      <AdvancedFilterModal open={advancedOpen} onClose={() => setAdvancedOpen(false)} vehicles={vehicles} />

      <style>{`
        @media (max-width: 768px) {
          .ws-cars-sidebar { display: none !important; }
          .ws-cars-layout { flex-direction: column !important; }
        }
      `}</style>
    </div>
  );
}

export default WebsiteUsedCars;
